import { Button, FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { produce } from 'immer';
import {
  FlowMessengerAction,
  SubflowMessengerAction,
} from 'lib-shared-types-api';
import noop from 'lodash/noop';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { v4 as uuid } from 'uuid';

import { useCustomForms } from 'app/views/layout/views/settings/views/customForms/hooks/useCustomForms/useCustomForms';
import { FlowContext } from 'app/views/layout/views/settings/views/messenger/views/editFlow/flowEditorRoot';
import { AppLink } from 'components/appLink';
import { FlowExtraDataFetch } from 'components/powerFlowShared/components/extraDataFetch';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import {
  FlowMessengerNodeSubflowReference,
  FlowMessengerNodeSubflowReturn,
} from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import { getSubflowNodesBasedOnFeatureFlags } from 'components/settings/components/sections/dixaMessenger/views/flow/getNodesBasedOnFeatureFlags';
import { DixaMessengerSubflow } from 'components/settings/components/sections/dixaMessenger/views/flow/hooks';
import { FlowEditorChrome, FlowType, NodeDataT } from 'fable/flowEditor';
import { OnLeaveHandler } from 'fable/flowEditor/components/flowEditorChrome/flowEditorChrome';
import { FlowT } from 'hocs/flows/api/types';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { FloatingBanner } from './floatingBanner/floatingBanner';
import {
  genericValidationError,
  useSubflowValidation,
} from './subflowValidation';

function getReferencesToCurrentSubflow(
  subflowId: string,
  mainFlow: FlowT<FlowMessengerAction, SubflowMessengerAction>,
): FlowMessengerNodeSubflowReference[] {
  return (
    mainFlow?.actions?.filter(
      (action): action is FlowMessengerNodeSubflowReference =>
        action.nodeType === FlowNodeTypes.SUBFLOW_REFERENCE &&
        action.payload.subflowId === subflowId,
    ) ?? []
  );
}

export const SubflowEditor = (): JSX.Element => {
  const { forms } = useCustomForms();
  const {
    isLoading,
    subflows,
    actions,
    setSubflows,
    initialFlow,
    flowId,
    setActions,
    mainFlowSaveStatusData,
    highlightedNodes,
  } = useContext(FlowContext);

  const { subflowId } = useAppPathParams(
    '/settings/dixa-messenger/flow/:flowId/sub-flow/edit/:subflowId',
  );

  const subflowIndex = subflows?.findIndex(({ id }) => id === subflowId);
  const subflow = subflowIndex !== undefined ? subflows[subflowIndex] : null;

  const getInitialSubflow = useCallback(() => {
    if (subflow?.actions && highlightedNodes && highlightedNodes.length > 0) {
      const initNodeIndex = subflow.actions.findIndex(
        (node) => node.nodeType === FlowNodeTypes.INIT,
      );

      if (initNodeIndex === -1) {
        return null;
      }

      const initNode = subflow.actions[initNodeIndex];

      const updatedSubflow = produce(subflow, (draft) => {
        // set the sub flow init node to the same position as the main flow init node in order to make the editor look nice
        const { actions } = draft;

        const updateRootChild = (
          node: SubflowMessengerAction,
          rootNodeId: string,
        ) => {
          const updateParentId = (
            node: SubflowMessengerAction,
          ): SubflowMessengerAction => {
            return produce(node, (draftNode) => {
              draftNode.parentId = rootNodeId;
            });
          };

          const updatedNode = updateParentId(node);
          actions.push(updatedNode as SubflowMessengerAction);
        };

        const updateRootNode = (
          node: SubflowMessengerAction,
          newNodeId: string,
        ) => {
          const updateParentId = (
            node: SubflowMessengerAction,
          ): SubflowMessengerAction => {
            return produce(node, (draftNode) => {
              // the first highlighted node's parent id must refer to the init sub flow node
              draftNode.parentId = initNode.id;
              // the uuid is also updated to prevent conflicts
              // as it was reused by the subflowreference node
              draftNode.id = newNodeId;
            });
          };

          const updatedNode = updateParentId(node);
          actions.push(updatedNode as SubflowMessengerAction);

          const findParentOfFirstHighlightedNode = () => {
            return initialFlow?.actions.find(
              (action) => action.id === node.parentId,
            );
          };

          const parentOfFirstHighlightedNode =
            findParentOfFirstHighlightedNode();

          const setInitialNodePosition = () => {
            actions[initNodeIndex].pageX =
              parentOfFirstHighlightedNode?.pageX ?? 0;
            actions[initNodeIndex].pageY =
              parentOfFirstHighlightedNode?.pageY ?? 0;
          };

          // set the init sub flow node to the same position as the parent for the first highlighted node
          setInitialNodePosition();
        };

        const rootNodeId = uuid();
        highlightedNodes.forEach((node, index) => {
          // the highlighted nodes array are sorted so that first element is the ancestor(root node)
          if (index === 0) {
            updateRootNode(node, rootNodeId);
          } else if (index === 1) {
            updateRootChild(node, rootNodeId);
          } else {
            actions.push(node);
          }
        });
      });

      return updatedSubflow;
    }
    return subflow;
  }, [highlightedNodes, initialFlow, subflow]);

  const [editedSubFlow, setEditedSubFlow] =
    useState<DixaMessengerSubflow | null>(getInitialSubflow);

  const { invalidNodeIds, triggerValidation } = useSubflowValidation(
    editedSubFlow?.actions ?? [],
  );

  // Compute the edited subflow based on the highlighted nodes and set the initial value
  useEffect(() => {
    setEditedSubFlow(getInitialSubflow());
  }, [getInitialSubflow]);

  const featureFlags = useFeatureFlag([
    'integrations-for-dixa-messenger',
    'conversation-assistant-for-dixa-messenger',
    'chatbots-integration-settings',
    'has-custom-forms-feature',
    'has-custom-forms-v2-feature',
    'subflows-in-dixa-messenger-flow-builder',
  ]);

  const nodeTypes = getSubflowNodesBasedOnFeatureFlags(featureFlags);

  /**
   * Updates the payload of the subflow reference nodes in the main flow to
   * include exits for each return node in the current subflow
   */
  const updateSubflowReferenceExits = useCallback(
    (subflowId: string) => {
      const currentSubflow = editedSubFlow;
      if (!currentSubflow || !initialFlow) {
        return;
      }

      const currentSubflowReturnNodes =
        currentSubflow.actions.filter(
          (action): action is FlowMessengerNodeSubflowReturn =>
            action.nodeType === FlowNodeTypes.SUBFLOW_RETURN,
        ) ?? [];

      const referencesToThisSubflowFromTheMainFlow =
        getReferencesToCurrentSubflow(subflowId, initialFlow);

      // For each reference to this subflow from the main flow, add exits for each return node in the subflow
      referencesToThisSubflowFromTheMainFlow.forEach((referenceNode) => {
        const referenceNodeIndex = initialFlow.actions.findIndex(
          (action) => action.id === referenceNode.id,
        );

        if (referenceNodeIndex === undefined || referenceNodeIndex < 0) {
          return;
        }

        const updatedExitsReferenceNode = produce(
          referenceNode,
          (referenceNodeDraft: FlowMessengerNodeSubflowReference) => {
            currentSubflowReturnNodes.forEach((returnNode) => {
              const returnNodeIdentifier = returnNode.payload.returnId;

              const exitAlreadyExists =
                referenceNodeDraft.payload.exits.hasOwnProperty(
                  returnNodeIdentifier,
                );

              // If the exit does not exist, create a new one and set it to null since we don't know what it will connect to it yet
              if (!exitAlreadyExists) {
                referenceNodeDraft.payload.exits[returnNodeIdentifier] = {
                  nextNodeId: uuid(),
                };
              }
            });
          },
        );

        const newActions = produce(initialFlow.actions, (draft) => {
          draft[referenceNodeIndex] = updatedExitsReferenceNode;
        });

        setActions(newActions);
      });
    },
    [editedSubFlow, initialFlow, setActions],
  );

  // if the subflow is referenced more than once in the flow, show a info message to the user
  const usageInfo = useMemo<string | undefined>(() => {
    if (!initialFlow) {
      return undefined;
    }

    const refsToThisSubflow = getReferencesToCurrentSubflow(
      subflowId,
      initialFlow,
    );

    if (refsToThisSubflow.length > 1) {
      return `Used ${refsToThisSubflow.length} times in the main flow`;
    }

    return undefined;
  }, [subflowId, initialFlow]);

  const handleSave = useCallback(() => {
    if (!flowId) {
      return;
    }

    // when subflow is "saved", the main flow should be marked as unsaved
    // Because the subflow is only presisted on backend when the main flow is saved
    mainFlowSaveStatusData.setStatusToUnsaved();

    // Update the subflow reference node in the main flow to include exits for each return node in the subflow
    updateSubflowReferenceExits(subflowId);

    // update the subflow list in the main flow object with the edited subflow
    const subflowsButTheCurrentOne = subflows.filter(
      ({ id }) => id !== subflowId,
    );

    setSubflows([
      ...subflowsButTheCurrentOne,
      editedSubFlow as DixaMessengerSubflow,
    ]);
  }, [
    editedSubFlow,
    flowId,
    mainFlowSaveStatusData,
    setSubflows,
    subflowId,
    subflows,
    updateSubflowReferenceExits,
  ]);

  useDebounce(triggerValidation, 300, [editedSubFlow?.actions]);

  const handleChange = useCallback(
    (changedFlow: NodeDataT): void => {
      setEditedSubFlow(changedFlow as DixaMessengerSubflow);
    },
    [setEditedSubFlow],
  );

  const handleLeave: OnLeaveHandler = () => {
    const result = triggerValidation();

    if (result.length > 0) {
      return { isAllowedToLeave: false };
    }

    handleSave();

    return { isAllowedToLeave: true };
  };

  if (isLoading) {
    return (
      <FlexBox
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <Spinner />
      </FlexBox>
    );
  }

  if (!editedSubFlow) {
    return (
      <FlexBox
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        padding="400"
        gap="300"
      >
        <Text.Heading variant="heading-3">Sub-flow not found</Text.Heading>

        {flowId && (
          <AppLink
            path="/settings/dixa-messenger/flow/:flowId"
            params={{ path: { flowId } }}
          >
            <Button variant="tertiary">Back to main flow</Button>
          </AppLink>
        )}
      </FlexBox>
    );
  }

  const hasInvalidNodes = invalidNodeIds.length > 0;

  return (
    <FlowExtraDataFetch
      actions={actions}
      render={(extraData) => (
        <FlowEditorChrome
          backgroundColor={colors.purple[100]}
          infoMessage={usageInfo}
          backButtonProps={{ disabled: hasInvalidNodes }}
          errorMessage={null}
          warningMessage={hasInvalidNodes ? genericValidationError : undefined}
          extraData={{
            name: editedSubFlow?.name ?? 'Subflow',
            subflows: undefined,
            nodeTypes,
            forms,
            ...extraData,
          }}
          backButtonText="Back to main flow"
          flowType={FlowType.FLOW_TYPE_MESSENGER}
          goBackPath={`/settings/dixa-messenger/flow/${flowId}`}
          initialData={editedSubFlow}
          invalidNodes={invalidNodeIds}
          nodeTypes={nodeTypes}
          onActivate={noop} // a subflow cannot be de-activated
          topbarExtraItems={<FloatingBanner />}
          onChange={handleChange}
          onLeave={handleLeave}
          showActivateButton={false}
        />
      )}
    />
  );
};
